import { useEffect, useState } from 'react';

import { UploadedFile } from '@backrs_ui/common-ui';

import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { profileApi, StoryDocument } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { checkUserDocuments } from '@backrs_ui/grow-ui/src/redux/user/thunks/userDocument.thunk';

import { Box, Theme, SxProps } from '@mui/material';

import { ConfirmDelete } from '../atoms/ConfirmDelete';
import { ProfileFormDocument } from '../ProfileFormDocument';
import { FileUploadConfig } from '../types/profile.types';

const MEDIA_TYPE_TO_FILE_EXTENSION: Record<StoryDocument['media_type'], string> = {
  text: 'txt',
  document: 'pdf',
  video: 'mp4',
  pdf: 'pdf',
  docx: 'docx',
  image: 'png',
};

type ProfileDocumentViewProps = Readonly<{
  fileUploadConfig: FileUploadConfig;
  storyDocuments: StoryDocument[];
}>;

export const ProfileDocumentView = ({
  fileUploadConfig,
  storyDocuments,
}: ProfileDocumentViewProps) => {
  const [documents, setDocuments] = useState(storyDocuments);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<StoryDocument | null>(null);

  const dispatch = useAppDispatch();

  const handleDeleteClick = (document: StoryDocument) => {
    setSelectedDocument(document);
    setOpenDialog(true);
  };

  const handleDeleteSuccess = (deletedId: number) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== deletedId));

    // now invalidate the user profile
    dispatch(profileApi.util.invalidateTags(['UserProfile']));

    dispatch(checkUserDocuments());
  };

  useEffect(() => {
    setDocuments(storyDocuments);

    dispatch(checkUserDocuments());
  }, [storyDocuments, dispatch]);

  return (
    <Box>
      <ConfirmDelete
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedDocument={selectedDocument}
        onSuccess={handleDeleteSuccess}
      />

      {(!fileUploadConfig.overrideModal || documents.length === 0) && (
        <ProfileFormDocument
          fileUploadConfig={fileUploadConfig}
          onError={(error) => {
            console.error('Error uploading document:', error);
          }}
        />
      )}
      <Box sx={documentsContainerSx}>
        {documents.map((document) => {
          const isPurposeVideo = document.question_id === 'video';

          // override to force mp4 if it's a purpose video
          const fileType = isPurposeVideo
            ? 'mp4'
            : MEDIA_TYPE_TO_FILE_EXTENSION[document.media_type];

          const fileName = isPurposeVideo ? 'My Intro Video' : document.title;
          const fileSize = isPurposeVideo
            ? new Date(document.updated_at || '').getFullYear().toString()
            : document.description;

          return (
            <Box
              key={document.id}
              sx={documentContainerSx}
              // NOTE: WHAT SHOULD BEHAVIOR BE FORE PROFILE VIDEO?
              onClick={() => window.open(document.media_url, '_blank')}
            >
              <UploadedFile
                fileName={fileName}
                fileSize={fileSize}
                fileType={fileType}
                handleDelete={() => handleDeleteClick(document)}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const documentsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  mt: backrs.spacing['spacing-lg'],
});

const documentContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};
